import * as React from 'react'
import { CalendarIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Input } from '@/components/ui/input'

interface DateTimePickerProps {
  date?: string // YYYY-MM-DD format
  time?: string // HH:mm format
  onDateChange?: (date: string) => void
  onTimeChange?: (time: string) => void
  dateLabel?: string
  timeLabel?: string
  datePlaceholder?: string
  disabled?: boolean
  required?: boolean
  className?: string
}

export function DateTimePicker({
  date,
  time,
  onDateChange,
  onTimeChange,
  dateLabel,
  timeLabel,
  datePlaceholder = 'Select date',
  disabled = false,
  required = false,
  className,
}: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false)
  const selectedDate = date ? new Date(date) : undefined

  const handleDateSelect = (newDate: Date | undefined) => {
    if (newDate) {
      // Format as YYYY-MM-DD
      const formatted = newDate.toISOString().split('T')[0]
      onDateChange?.(formatted)
      setOpen(false)
    } else {
      onDateChange?.('')
      setOpen(false)
    }
  }

  return (
    <div className={cn('flex gap-2', className)}>
      <div className="flex flex-col gap-2 flex-1">
        {dateLabel && <span className="text-sm font-medium">{dateLabel}</span>}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                'justify-start font-normal',
                !date && 'text-muted-foreground',
              )}
              disabled={disabled}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {date ? selectedDate?.toLocaleDateString() : datePlaceholder}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto overflow-hidden p-0" align="start">
            <Calendar
              mode="single"
              selected={selectedDate}
              captionLayout="dropdown"
              onSelect={handleDateSelect}
              disabled={disabled}
              fromYear={2020}
              toYear={2030}
            />
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex flex-col gap-2">
        {timeLabel && <span className="text-sm font-medium">{timeLabel}</span>}
        <Input
          type="time"
          value={time || ''}
          onChange={(e) => {
            // Only call onTimeChange if the value is not empty
            // This prevents sending "00:00" as a default value
            const newValue = e.target.value
            onTimeChange?.(newValue || '')
          }}
          disabled={disabled}
          required={required}
          className="w-[120px] bg-background"
        />
      </div>
    </div>
  )
}
